import {
    switchChain,
    waitForTransactionReceipt,
} from 'wagmi/actions';

import { HYPER_EVM_CHAIN_ID, walletWagmiConfig } from '@/features/wallet';

/**
 * Switches the connected wallet to HyperEVM before an EOA-direct transaction.
 *
 * This must be EXPLICIT: wagmi's `writeContract({ chainId })` does not switch the wallet for
 * plain injected connectors — it asserts the chain and throws `ChainMismatchError` — and the
 * app keeps wallets on Cronos, so without this every extension-wallet user failed before the
 * first signature prompt. A refusal is translated into a plain instruction.
 */
export async function ensureHyperEvmChain(): Promise<void> {
    try {
        await switchChain(walletWagmiConfig, { chainId: HYPER_EVM_CHAIN_ID });
    } catch {
        throw new Error(
            'Switch your wallet to the HyperEVM network to continue.',
        );
    }
}

/**
 * Waits for a HyperEVM receipt and REJECTS reverted transactions.
 *
 * `waitForTransactionReceipt` resolves on `status: 'reverted'`; treating that as a completed
 * step let a run continue (or end "success") past a leg that moved nothing.
 */
export async function waitForSuccessfulHyperEvmReceipt(
    hash: `0x${string}`,
    label: string,
): Promise<void> {
    const receipt = await waitForTransactionReceipt(walletWagmiConfig, {
        hash,
        chainId: HYPER_EVM_CHAIN_ID,
    });
    if (receipt.status !== 'success') {
        throw new Error(`${label} transaction reverted.`);
    }
}
